import { useCallback, useEffect, useState } from 'react';

import { FortnightValue, TimeScope, TimeScopeMode, TimeScopeView } from './types';
import { adjustScopeForConstraints, getDefaultTimeScopeValues } from './utils';
import { DaysView } from './views/DayView';
import { FortnightView } from './views/FortnightView';
import { MonthView } from './views/MonthView';
import { WeekView } from './views/WeekView';
import { YearView } from './views/YearView';
import { t } from '../../services/translation/translation';
import { addMonths, getMonth, getYear, MonthValue, subtractMonths } from '../../utils/date';
import { Label } from '../atomic/Label';
import { OPTIONS, SelectInput } from '../atomic/SelectInput';
import { SecondaryButton } from '../button/SecondaryButton';
import { TransparentButton } from '../button/TransparentButton';

const DEFAULT_VIEW_FOR_SCOPE: Record<TimeScopeMode, TimeScopeView> = {
  day: 'days',
  week: 'weeks',
  month: 'months',
  fortnight: 'fortnights',
  year: 'years',
};

const modes: OPTIONS[] = [
  { label: t('common.day'), value: 'day' },
  { label: t('common.week'), value: 'week' },
  { label: t('common.month'), value: 'month' },
  { label: t('common.fortnight'), value: 'fortnight' },
  { label: t('common.year'), value: 'year' },
];

type Props = {
  mode: TimeScopeMode;
  onScopeChange: (_scope?: TimeScope, _mode?: TimeScopeMode) => void;
  defaultTimeScope?: TimeScope;
  maxDate?: number;
  minDate?: number;
  hideActions?: boolean;
  scope: TimeScope;
  hideChangeMode?: boolean;
};

export const TimeScopePicker = ({
  mode,
  onScopeChange,
  defaultTimeScope,
  maxDate,
  minDate,
  hideActions,
  hideChangeMode,
  scope,
}: Props) => {
  const [displayScope, setDisplayScope] = useState<TimeScope>(adjustScopeForConstraints(scope, minDate, maxDate));
  const [displayMode, setDisplayMode] = useState(mode);
  const [view, setView] = useState<TimeScopeView>(DEFAULT_VIEW_FOR_SCOPE[displayMode]);

  const handlePreviousYear = () =>
    setDisplayScope(prev => adjustScopeForConstraints({ ...prev, year: prev.year - 1 }, minDate, maxDate));
  const handleNextYear = () =>
    setDisplayScope(prev => adjustScopeForConstraints({ ...prev, year: prev.year + 1 }, minDate, maxDate));
  const handlePreviousMonth = () => {
    const date = new Date(displayScope.year, displayScope.month).valueOf();
    const nextDate = subtractMonths(date, 1);

    setDisplayScope(prev =>
      adjustScopeForConstraints({ ...prev, year: getYear(nextDate), month: getMonth(nextDate) }, minDate, maxDate),
    );
  };
  const handleNextMonth = () => {
    const date = new Date(displayScope.year, displayScope.month).valueOf();
    const nextDate = addMonths(date, 1);

    setDisplayScope(prev =>
      adjustScopeForConstraints({ ...prev, year: getYear(nextDate), month: getMonth(nextDate) }, minDate, maxDate),
    );
  };
  const handleSelectYear = (year: number) => {
    const newValue = adjustScopeForConstraints({ ...displayScope, year }, minDate, maxDate);

    setDisplayScope(newValue);

    if (displayMode === 'year') {
      handleScopeChange(newValue);
    } else if (displayMode === 'week') {
      setView('weeks');
    } else if (displayMode === 'day' || displayMode === 'month' || displayMode === 'fortnight') {
      setView('months');
    }
  };
  const handleSelectMonth = (month: MonthValue) => {
    const newValue = adjustScopeForConstraints({ ...displayScope, month }, minDate, maxDate);

    setDisplayScope(newValue);

    if (displayMode === 'month') {
      handleScopeChange(newValue);
    } else if (displayMode === 'fortnight') {
      setView('fortnights');
    } else if (displayMode === 'day') {
      setView('days');
    }
  };
  const handleSelectFortnight = (fortnight: FortnightValue) => {
    const newValue = adjustScopeForConstraints({ ...displayScope, fortnight }, minDate, maxDate);

    handleScopeChange(newValue);
  };
  const handleSelectWeek = (week: number) => {
    const newValue = adjustScopeForConstraints({ ...displayScope, week }, minDate, maxDate);

    handleScopeChange(newValue);
  };
  const handleSelectDay = (day: number) => {
    const newValue = adjustScopeForConstraints({ ...displayScope, day }, minDate, maxDate);

    handleScopeChange(newValue);
  };
  const handleResetScope = () => {
    const initialValue = adjustScopeForConstraints(defaultTimeScope ?? getDefaultTimeScopeValues(), minDate, maxDate);

    onScopeChange(undefined, undefined);
    setDisplayScope(initialValue);
  };

  const handleScopeChange = useCallback(
    (newValue: TimeScope) => {
      setDisplayScope(newValue);
      onScopeChange(newValue, displayMode);
    },
    [displayMode, onScopeChange],
  );

  const handleCurrentScope = () => {
    const currentValue = adjustScopeForConstraints(getDefaultTimeScopeValues(), minDate, maxDate);
    handleScopeChange(currentValue);
  };

  useEffect(() => {
    setDisplayScope(scope);
    setView(DEFAULT_VIEW_FOR_SCOPE[mode]);
    setDisplayMode(mode);
  }, [mode, scope]);

  return (
    <div className="flex w-64 gap-2 flex-wrap">
      {!hideChangeMode ? (
        <>
          <div className="flex items-center justify-center w-full gap-2">
            <Label className="mb-0">{t('common.period-type')}</Label>
            <SelectInput
              options={modes}
              notNullable
              containerClassName="min-w-[106px]"
              value={displayMode}
              onChange={e => {
                setDisplayMode(e.currentTarget.value as TimeScopeMode);
                setView(DEFAULT_VIEW_FOR_SCOPE[e.currentTarget.value as TimeScopeMode]);
              }}
            />
          </div>
          <div className="h-[1px] w-full bg-gray-100 dark:bg-gray-600" />
        </>
      ) : null}
      {view === 'days' ? (
        <DaysView
          displayYear={displayScope.year}
          displayMonth={displayScope.month}
          selectedDay={
            mode === 'day' && displayScope.year === scope.year && displayScope.month === scope.month
              ? scope.day
              : undefined
          }
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          onSelectDay={handleSelectDay}
          onHeaderClick={() => setView('months')}
          maxDate={maxDate}
          minDate={minDate}
        />
      ) : view === 'weeks' ? (
        <WeekView
          displayYear={displayScope.year}
          selectedWeek={mode === 'week' && displayScope.year === scope.year ? scope.week : undefined}
          onNextYear={handleNextYear}
          onPreviousYear={handlePreviousYear}
          onSelectWeek={handleSelectWeek}
          onHeaderClick={() => setView('years')}
          maxDate={maxDate}
          minDate={minDate}
        />
      ) : view === 'fortnights' ? (
        <FortnightView
          displayYear={displayScope.year}
          displayMonth={displayScope.month}
          selectedFornight={
            mode === 'fortnight' && displayScope.year === scope.year && displayScope.month === scope.month
              ? scope.fortnight
              : undefined
          }
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          onHeaderClick={() => setView('months')}
          onSelectFortnight={handleSelectFortnight}
          maxDate={maxDate}
          minDate={minDate}
        />
      ) : view === 'months' ? (
        <MonthView
          displayYear={displayScope.year}
          selectedMonth={mode === 'month' && displayScope.year === scope.year ? scope.month : undefined}
          onNextYear={handleNextYear}
          onPreviousYear={handlePreviousYear}
          onSelectMonth={handleSelectMonth}
          onHeaderClick={() => setView('years')}
          maxDate={maxDate}
          minDate={minDate}
        />
      ) : view === 'years' ? (
        <YearView
          selectedYear={mode === 'year' ? scope.year : undefined}
          initialYear={scope.year}
          onSelectYear={handleSelectYear}
          maxDate={maxDate}
          minDate={minDate}
        />
      ) : null}

      {hideActions ? null : (
        <div className="flex space-x-2 mt-2 w-full">
          <SecondaryButton className="w-full" onClick={handleCurrentScope}>
            {t('common.today')}
          </SecondaryButton>

          <TransparentButton className="w-full" onClick={handleResetScope}>
            {t('common.reset')}
          </TransparentButton>
        </div>
      )}
    </div>
  );
};
